import { Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FeatureFlags } from '../../../../../../../../global/featureFlags/featureFlags';
import { ButtonComponent } from '../../../../../../../../shared/components/button/button.component';
import { TextAreaComponent } from '../../../../../../../../shared/components/forms/text-area/text-area.component';
import { TelephoneLinkComponent } from '../../../../../../../../shared/components/telephone-link/telephone-link.component';
import { OrbitFormClaimService } from '../../../../../services/orbit-form-claim.service';
import {
  VulnerableCustomerQuestionsAnswers,
  VulnerableCustomerQuestionsAnswersRequest,
  VulnerableCustomersForm,
  VulnerableCustomersFormValues,
} from './vulnerable-customers.interface';

@Component({
  selector: 'app-vulnerable-customers',
  imports: [TextAreaComponent, ButtonComponent, FormsModule, TelephoneLinkComponent],
  templateUrl: './vulnerable-customers.component.html',
  styleUrl: './vulnerable-customers.component.scss',
  host: {
    class: 'full-width max-w-760 mx-auto',
  },
})
export class VulnerableCustomersComponent {
  constructor(private readonly orbitFormClaimService: OrbitFormClaimService) {}

  open = signal(false);
  submitClicked = false;
  showWarningMessage = false;
  needsSubmitted = false;
  buttonsHidden = false;
  formBlank = signal<any>(undefined);
  answersPayload = signal<any>(undefined);

  private viewModel = computed(() => {
    const { bundle, mandatoryData } = this.orbitFormClaimService.state.claimData;
    const bookingData = this.orbitFormClaimService.bookingData();
    return {
      isLoading: this.orbitFormClaimService.vcCaptureLoading(),
      error: this.orbitFormClaimService.vcFormError(),
      formBlank: this.formBlank(),
      bookingRef: bookingData?.claimID,
      planNumber: bundle?.planNumber,
      faultDate: this.orbitFormClaimService.faultStartDate(),
      applianceModelNumber: mandatoryData?.modelNumber || bookingData?.modelNumber,
      open: this.open(),
      answersPayload: this.answersPayload(),
      claimTypeWord: bundle?.claimType === 'SERVICE' ? 'service' : 'repair',
    };
  });

  get vm() {
    return this.viewModel();
  }

  saveAndSubmit() {
    this.submitClicked = true;
    if (!this.formIsEmpty) {
      this.showWarningMessage = true;
      this.buttonsHidden = true;
    }
  }

  handleSubmit() {
    const answersPayload = this.buildQAPayload();
    if (!this.formIsEmpty && answersPayload) {
      const fullPayload = this.buildFullPayload(answersPayload.qa);
      this.orbitFormClaimService.vcPayload.set(fullPayload);

      const isCustomerSupport = FeatureFlags.getFeatureFlag('customer-support-vc-capture');
      this.orbitFormClaimService.submitVCCaptureForm(isCustomerSupport);
      this.needsSubmitted = true;
    }
  }

  goBack() {
    this.showWarningMessage = false;
    this.buttonsHidden = false;
  }

  buildQAPayload(): VulnerableCustomerQuestionsAnswers {
    const qa: { q: string; a: string }[] = [];
    this.vulnerableCustomersForm.forEach((section) => {
      if (section.options) {
        section.options.forEach((option) => {
          let checked = false;
          if (section.name === 'homeVisits' || section.name === 'medicalNeeds') {
            checked = (this.formValues[section.name] as Record<string, boolean>)[option.value];
          }
          qa.push({
            q: option.label,
            a: checked ? 'yes' : 'no',
          });
        });
      }

      if (section.name === 'otherNeeds') {
        const text = this.formValues.otherNeeds?.trim();
        qa.push({
          q: section.title,
          a: text ? text : 'blank',
        });
      }
    });
    return { qa };
  }

  buildFullPayload(qa: Array<{ q: string; a: string }>): VulnerableCustomerQuestionsAnswersRequest {
    return {
      notificationId: 'claims-vuln-customer-survey',
      data: {
        context: {
          bookingRef: this.vm?.bookingRef,
          planNumber: this.vm?.planNumber,
          faultDate: (this.vm?.faultDate ?? this.getFaultDateByBooking()) as string,
          applianceModelNumber: this.vm?.applianceModelNumber,
          submissionTime: new Date().toString(),
        },
        qa,
      },
    };
  }

  getFaultDateByBooking() {
    const bookingData = this.orbitFormClaimService.bookingData();
    return bookingData?.claimQuestionsArray?.find((x: any) => x.answerType === 'DATE')?.answerValue;
  }

  get formIsEmpty() {
    const { otherNeeds, ...checkboxes } = this.formValues;

    const hasText = !!otherNeeds?.trim();
    const checkboxesSelected = Object.values(checkboxes).some((group) => group && Object.values(group).includes(true));

    return !hasText && !checkboxesSelected;
  }

  vulnerableCustomersForm: VulnerableCustomersForm = [
    {
      title: 'Your home visits',
      description: 'Select any of these that you need when we visit you:',
      name: 'homeVisits',
      label: 'Your home visits',
      options: [
        {
          value: 'moreTime',
          label: 'Please allow more time for me to answer the door',
        },
        {
          value: 'ringBell',
          label: 'Please ring the doorbell when you arrive',
        },
      ],
    },
    {
      title: 'Your medical needs',
      description: 'Select any of these reasons that apply:',
      name: 'medicalNeeds',
      label: 'Your medical needs',
      options: [
        {
          value: 'needFridge',
          label: 'I need a fridge for medicine or allergen-free food',
        },
        {
          value: 'needAppliance',
          label: 'I need my cooking appliance for a special diet',
        },
        {
          value: 'needHeating',
          label: 'I need heating because of a health condition',
        },
        {
          value: 'needWashingMachine',
          label: 'I need my washing machine because of a health condition',
        },
      ],
    },
    {
      title: 'Do you have any other health or personal needs we should know about before visiting?',
      description: `Note: we can't change the time of your booking.`,
      name: 'otherNeeds',
      label: 'Other needs',
    },
  ];

  formValues: VulnerableCustomersFormValues = {
    homeVisits: {},
    medicalNeeds: {},
    otherNeeds: '',
  };
}
